import { useState, useEffect, useCallback, useRef } from 'react';
import './RecipePage.css';
import { supabase } from '../supabaseClient';
import { formatAmount, convertIngredient, convertTemp } from '../utils/unitConverter';
import { getFoodGroup } from '../utils/foodGroup';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';
import translations from '../translations';
import EditRecipePanel from './EditRecipePanel';
import AddToMealPlanModal from './AddToMealPlanModal';
import CookingNotes from './CookingNotes';
import CookingMode from './CookingMode';

function formatTime(mins) {
  if (!mins) return null;
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function RecipePage({ recipeId, onBack }) {
  const { user, isAdmin, isMember } = useAuth();
  const { language } = useLanguage();
  const t = translations[language];

  const [recipe, setRecipe] = useState(null);
  const [ingredients, setIngredients] = useState([]);
  const [steps, setSteps] = useState([]);
  const [nutrition, setNutrition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [servings, setServings] = useState(null);
  const [unitSystem, setUnitSystem] = useState('metric');
  const [tips, setTips] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [addingToList, setAddingToList] = useState(false);
  const [addedToList, setAddedToList] = useState(false);
  const [showMealPlanModal, setShowMealPlanModal] = useState(false);
  const [showCookingMode, setShowCookingMode] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Collection state
  const [showCollectionDropdown, setShowCollectionDropdown] = useState(false);
  const [collections, setCollections] = useState([]);
  const [recipeCollectionIds, setRecipeCollectionIds] = useState([]);
  const [collectionLoading, setCollectionLoading] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [creatingCollection, setCreatingCollection] = useState(false);
  const collectionDropdownRef = useRef(null);

  // Translation state
  const [translation, setTranslation] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [translateError, setTranslateError] = useState('');

  const fetchRecipe = useCallback(async () => {
    setLoading(true);
    const [recipeRes, ingredientsRes, stepsRes, nutritionRes] = await Promise.all([
      supabase.from('recipes').select('*').eq('id', recipeId).single(),
      supabase.from('recipe_ingredients').select('*').eq('recipe_id', recipeId).order('sort_order'),
      supabase.from('recipe_steps').select('*').eq('recipe_id', recipeId).order('step_number'),
      supabase.from('recipe_nutrition').select('*').eq('recipe_id', recipeId).maybeSingle(),
    ]);
    if (recipeRes.data) {
      setRecipe(recipeRes.data);
      setServings(recipeRes.data.default_servings);
      setTips(recipeRes.data.tips || []);
    }
    setIngredients(ingredientsRes.data || []);
    setSteps(stepsRes.data || []);
    setNutrition(nutritionRes.data || null);
    setLoading(false);
  }, [recipeId]);

  const fetchTranslation = useCallback(async () => {
    if (language !== 'hu') {
      setTranslation(null);
      return;
    }
    const { data } = await supabase
      .from('recipe_translations')
      .select('*')
      .eq('recipe_id', recipeId)
      .eq('language_code', 'hu')
      .maybeSingle();
    setTranslation(data || null);
  }, [recipeId, language]);

  useEffect(() => { fetchRecipe(); }, [fetchRecipe, refreshKey]);
  useEffect(() => { fetchTranslation(); }, [fetchTranslation, refreshKey]);

  useEffect(() => {
    if (!showCollectionDropdown) return;
    function handleClickOutside(e) {
      if (collectionDropdownRef.current && !collectionDropdownRef.current.contains(e.target)) {
        setShowCollectionDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCollectionDropdown]);

  async function handleOpenCollectionDropdown() {
    if (showCollectionDropdown) { setShowCollectionDropdown(false); return; }
    setCollectionLoading(true);
    setShowCollectionDropdown(true);
    let colQuery = supabase.from('collections').select('id, name').order('created_at');
    if (isAdmin) colQuery = colQuery.is('user_id', null);
    else if (isMember) colQuery = colQuery.eq('user_id', user?.id);
    const [{ data: cols }, { data: links }] = await Promise.all([
      colQuery,
      supabase.from('collection_recipes').select('collection_id').eq('recipe_id', recipeId)
    ]);
    setCollections(cols || []);
    setRecipeCollectionIds((links || []).map(l => l.collection_id));
    setCollectionLoading(false);
  }

  async function handleToggleCollection(collectionId) {
    const inCollection = recipeCollectionIds.includes(collectionId);
    if (inCollection) {
      await supabase.from('collection_recipes')
        .delete()
        .eq('collection_id', collectionId)
        .eq('recipe_id', recipeId);
      setRecipeCollectionIds(ids => ids.filter(id => id !== collectionId));
    } else {
      await supabase.from('collection_recipes')
        .insert({ collection_id: collectionId, recipe_id: recipeId });
      setRecipeCollectionIds(ids => [...ids, collectionId]);
    }
  }

  async function handleCreateAndAddCollection(e) {
    e.preventDefault();
    const name = newCollectionName.trim();
    if (!name) return;
    setCreatingCollection(true);

    // Auto-translate to Hungarian
    let name_hu = null;
    try {
      const res = await fetch('/.netlify/functions/translate-text', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: name, targetLanguage: 'hu' }),
      });
      const json = await res.json();
      if (json.translated) name_hu = json.translated;
    } catch (_) {}

    const { data } = await supabase.from('collections')
      .insert({ name, name_hu, user_id: isMember ? user?.id : null }).select().single();
    if (data) {
      await supabase.from('collection_recipes')
        .insert({ collection_id: data.id, recipe_id: recipeId });
      setCollections(prev => [...prev, data]);
      setRecipeCollectionIds(prev => [...prev, data.id]);
      setNewCollectionName('');
    }
    setCreatingCollection(false);
  }

  async function handleTranslate() {
    setTranslating(true);
    setTranslateError('');
    try {
      const res = await fetch('/.netlify/functions/translate-recipe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: recipe.title,
          description: recipe.description,
          dietary_tags: recipe.dietary_tags || [],
          equipment: recipe.equipment || [],
          storage_instructions: recipe.storage_instructions || '',
          notes: recipe.notes || '',
          ingredients: ingredients.map(ing => ({
            ingredient_name: ing.ingredient_name,
            prep_notes: ing.prep_notes,
          })),
          steps: steps.map(s => ({ instruction: s.instruction })),
          tips: tips,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setTranslateError(data.error || 'Translation failed.');
        setTranslating(false);
        return;
      }
      const { error: upsertErr } = await supabase
        .from('recipe_translations')
        .upsert({
          recipe_id: recipeId,
          language_code: 'hu',
          title: data.title,
          description: data.description,
          dietary_tags: data.dietary_tags?.length ? data.dietary_tags : null,
          equipment: data.equipment?.length ? data.equipment : null,
          storage_instructions: data.storage_instructions || null,
          notes: data.notes || null,
          ingredients: data.ingredients,
          steps: data.steps,
          tips: data.tips,
        }, { onConflict: 'recipe_id,language_code' });

      if (upsertErr) throw new Error(upsertErr.message);
      await fetchTranslation();
    } catch (err) {
      setTranslateError(err.message || 'Translation failed.');
    }
    setTranslating(false);
  }

  async function handleAddToShoppingList() {
    setAddingToList(true);

    const { data: existing } = await supabase
      .from('shopping_list_items')
      .select('*')
      .eq('checked', false);

    const existingMap = new Map();
    (existing || []).forEach(item => {
      const key = `${item.ingredient_name.toLowerCase().trim()}::${(item.unit || '').toLowerCase()}`;
      existingMap.set(key, item);
    });

    const toInsert = [];
    const toUpdate = [];
    const pendingMap = new Map();

    for (const ing of displayIngredients) {
      const scaled = (ing.amount != null)
        ? Math.round(ing.amount * scale * 100) / 100
        : null;
      const unit = ing.unit || null;
      const key = `${ing.ingredient_name.toLowerCase().trim()}::${(unit || '').toLowerCase()}`;

      if (existingMap.has(key)) {
        const ex = existingMap.get(key);
        const newAmt = (ex.amount != null && scaled != null)
          ? Math.round((ex.amount + scaled) * 100) / 100
          : (ex.amount ?? scaled);
        const existingTitles = ex.recipe_title ? ex.recipe_title.split(', ') : [];
        const titles = existingTitles.includes(recipe.title)
          ? ex.recipe_title
          : [...existingTitles, recipe.title].join(', ');
        toUpdate.push({ id: ex.id, amount: newAmt, recipe_title: titles });
        existingMap.set(key, { ...ex, amount: newAmt, recipe_title: titles });

      } else if (pendingMap.has(key)) {
        const pending = pendingMap.get(key);
        pending.amount = (pending.amount != null && scaled != null)
          ? Math.round((pending.amount + scaled) * 100) / 100
          : (pending.amount ?? scaled);

      } else {
        const item = {
          ingredient_name: ing.ingredient_name,
          amount: scaled,
          unit,
          food_group: getFoodGroup(ing.ingredient_name),
          recipe_id: recipeId,
          recipe_title: recipe.title,
          checked: false,
        };
        toInsert.push(item);
        pendingMap.set(key, item);
      }
    }

    if (toInsert.length) await supabase.from('shopping_list_items').insert(toInsert);
    for (const u of toUpdate) {
      await supabase.from('shopping_list_items')
        .update({ amount: u.amount, recipe_title: u.recipe_title })
        .eq('id', u.id);
    }

    setAddingToList(false);
    setAddedToList(true);
    setTimeout(() => setAddedToList(false), 3000);
  }

  function handleShareLink() {
    const url = `${window.location.origin}/recipe/${recipeId}`;
    navigator.clipboard.writeText(url).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await supabase.from('recipe_ingredients').delete().eq('recipe_id', recipeId);
      await supabase.from('recipe_steps').delete().eq('recipe_id', recipeId);
      await supabase.from('recipe_nutrition').delete().eq('recipe_id', recipeId);
      await supabase.from('recipe_translations').delete().eq('recipe_id', recipeId);
      await supabase.from('recipes').delete().eq('id', recipeId);

      if (recipe.image_url?.includes('/recipe-images/')) {
        const pathPart = recipe.image_url.split('/recipe-images/')[1];
        if (pathPart) {
          await supabase.storage.from('recipe-images').remove([pathPart]);
        }
      }

      onBack();
    } catch (_) {
      setDeleting(false);
      setConfirmDelete(false);
    }
  }

  async function handleTogglePublish() {
    if (!recipe) return;
    setPublishing(true);
    const newVal = !recipe.is_published;
    const { error } = await supabase.from('recipes').update({ is_published: newVal }).eq('id', recipeId);
    if (!error) setRecipe(prev => ({ ...prev, is_published: newVal }));
    setPublishing(false);
  }

  async function handleSendToSzilvia() {
    setSending(true);
    try {
      const { error } = await supabase.rpc('share_recipe_with_admin', { source_recipe_id: recipeId });
      if (error) throw error;
      setSent(true);
    } catch (e) {
      console.error('Send failed:', e);
    } finally {
      setSending(false);
    }
  }

  if (loading) {
    return <div className="recipe-page__loading"><p>{t.loadingRecipe}</p></div>;
  }
  if (!recipe) {
    return <div className="recipe-page__loading"><p>{t.recipeNotFound}</p></div>;
  }

  const scale = servings / recipe.default_servings;
  const totalTime = (recipe.prep_time_mins || 0) + (recipe.cook_time_mins || 0);

  // Can edit/delete: admin owns all null-user_id recipes; members own their own
  const isOwner = isAdmin
    ? !recipe.user_id  // admin owns the "official" recipes (no user_id)
    : isMember && recipe.user_id === user?.id;

  const displayTitle = (language === 'hu' && translation?.title) ? translation.title : recipe.title;
  const displayDescription = (language === 'hu' && translation?.description) ? translation.description : recipe.description;
  const displayIngredients = ingredients.map((ing, i) => {
    if (language === 'hu' && translation?.ingredients?.[i]) {
      return {
        ...ing,
        ingredient_name: translation.ingredients[i].ingredient_name || ing.ingredient_name,
        prep_notes: translation.ingredients[i].prep_notes ?? ing.prep_notes,
      };
    }
    return ing;
  });

  const displaySteps = steps.map((s, i) => {
    if (language === 'hu' && translation?.steps?.[i]) {
      return { ...s, instruction: translation.steps[i].instruction || s.instruction };
    }
    return s;
  });

  const displayTips = (language === 'hu' && translation?.tips?.length)
    ? translation.tips
    : tips;

  const displayDietaryTags = (language === 'hu' && translation?.dietary_tags?.length)
    ? translation.dietary_tags
    : (recipe.dietary_tags || []);

  const displayEquipment = (language === 'hu' && translation?.equipment?.length)
    ? translation.equipment
    : (recipe.equipment || []);

  const displayPersonalNote = (language === 'hu' && translation?.personal_note)
    ? translation.personal_note
    : recipe.personal_note;

  const displayStorage = (language === 'hu' && translation?.storage_instructions)
    ? translation.storage_instructions
    : recipe.storage_instructions;

  const displayNotes = (language === 'hu' && translation?.notes)
    ? translation.notes
    : recipe.notes;

  return (
    <div className="recipe-page">

      <button className="recipe-page__back" onClick={onBack} title={t.backToRecipes}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12"/>
          <polyline points="12 19 5 12 12 5"/>
        </svg>
      </button>

      {recipe.image_url && (
        <div className="recipe-page__hero">
          <img src={recipe.image_url} alt={displayTitle} />
        </div>
      )}

      <div className="recipe-page__header">
        <div className="recipe-page__title-row">
          <h1 className="recipe-page__title">{displayTitle}</h1>
          <div className="recipe-page__title-actions">

            {/* Unit toggle */}
            <div className="recipe-page__unit-toggle">
              <button className={unitSystem === 'metric' ? 'active' : ''} onClick={() => setUnitSystem('metric')}>{t.metric}</button>
              <button className={unitSystem === 'imperial' ? 'active' : ''} onClick={() => setUnitSystem('imperial')}>{t.imperial}</button>
            </div>

            {/* Shopping list */}
            {isAdmin && (
              <button
                className={`recipe-page__icon-btn${addedToList ? ' recipe-page__icon-btn--success' : ''}`}
                onClick={handleAddToShoppingList}
                disabled={addingToList || addedToList}
                title={addedToList ? t.addedToList : addingToList ? t.addingToList : t.addToShoppingList}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                  <line x1="3" y1="6" x2="21" y2="6"/>
                  <path d="M16 10a4 4 0 0 1-8 0"/>
                </svg>
              </button>
            )}

            {/* Meal plan */}
            {isAdmin && (
              <button
                className="recipe-page__icon-btn"
                onClick={() => setShowMealPlanModal(true)}
                title={t.addToMealPlan}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </button>
            )}

            {/* Collections */}
            {(isAdmin || isMember) && (
              <div className="recipe-page__collection-wrap" ref={collectionDropdownRef}>
                <button
                  className={`recipe-page__icon-btn${recipeCollectionIds.length > 0 ? ' recipe-page__icon-btn--active' : ''}`}
                  onClick={handleOpenCollectionDropdown}
                  title={t.addToCollection}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill={recipeCollectionIds.length > 0 ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
                  </svg>
                </button>
                {showCollectionDropdown && (
                  <div className="recipe-page__collection-dropdown">
                    {collectionLoading ? (
                      <p className="recipe-page__collection-loading">…</p>
                    ) : (
                      <>
                        {collections.length === 0 && (
                          <p className="recipe-page__collection-empty">{t.collectionsEmpty}</p>
                        )}
                        {collections.map(col => (
                          <label key={col.id} className="recipe-page__collection-item">
                            <input
                              type="checkbox"
                              checked={recipeCollectionIds.includes(col.id)}
                              onChange={() => handleToggleCollection(col.id)}
                            />
                            {(language === 'hu' && col.name_hu) ? col.name_hu : col.name}
                          </label>
                        ))}
                        <form className="recipe-page__collection-new" onSubmit={handleCreateAndAddCollection}>
                          <input
                            type="text"
                            placeholder={t.collectionNamePlaceholder}
                            value={newCollectionName}
                            onChange={e => setNewCollectionName(e.target.value)}
                            className="recipe-page__collection-input"
                          />
                          <button type="submit" disabled={creatingCollection || !newCollectionName.trim()} className="recipe-page__collection-create">
                            {t.createCollection}
                          </button>
                        </form>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Copy link */}
            <button
              className={`recipe-page__icon-btn${linkCopied ? ' recipe-page__icon-btn--success' : ''}`}
              onClick={handleShareLink}
              title={linkCopied ? t.shareRecipeCopied : t.shareRecipe}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
              </svg>
            </button>

            {/* Send to Szilvia */}
            {isMember && isOwner && (
              <button
                className={`recipe-page__send-btn${sent ? ' recipe-page__send-btn--sent' : ''}`}
                onClick={handleSendToSzilvia}
                disabled={sending || sent}
                title={sent ? 'Sent to Szilvia!' : 'Send to Szilvia'}
              >
                {sent ? '✓ Sent!' : sending ? 'Sending…' : '📤 Send to Szilvia'}
              </button>
            )}

            {/* Publish / Unpublish */}
            {isAdmin && isOwner && (
              <button
                className={`recipe-page__publish-btn${recipe.is_published ? ' recipe-page__publish-btn--live' : ' recipe-page__publish-btn--draft'}`}
                onClick={handleTogglePublish}
                disabled={publishing}
                title={recipe.is_published ? 'Click to unpublish (hide from public)' : (recipe.image_url ? 'Publish — make visible to public' : 'Add an image before publishing')}
              >
                {publishing ? '…' : recipe.is_published ? '● Live' : '○ Draft'}
              </button>
            )}

            {/* Edit */}
            {isOwner && (
              <button className="recipe-page__icon-btn" onClick={() => setShowEdit(true)} title={t.editRecipe}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </button>
            )}

            {/* Delete */}
            {isOwner && !confirmDelete && (
              <button className="recipe-page__icon-btn recipe-page__icon-btn--danger" onClick={() => setConfirmDelete(true)} title={t.deleteRecipe}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                </svg>
              </button>
            )}
            {isOwner && confirmDelete && (
              <div className="recipe-page__delete-confirm">
                <span>{t.deleteConfirm}</span>
                <button className="recipe-page__delete-confirm-yes" onClick={handleDelete} disabled={deleting}>
                  {deleting ? t.deleting : t.yesDelete}
                </button>
                <button className="recipe-page__delete-confirm-no" onClick={() => setConfirmDelete(false)} disabled={deleting}>
                  {t.cancel}
                </button>
              </div>
            )}

          </div>
        </div>

        {displayDescription && (
          <p className="recipe-page__description">{displayDescription}</p>
        )}

        <div className="recipe-page__tags">
          {recipe.is_traditional && (
            <span className="tag tag--traditional" title={t.traditionalTooltip}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight:'4px',verticalAlign:'middle'}}>
                <path d="M12 2C10.5 4.5 7 5 5 4c1 3 0 6-2 7.5 2 .5 3.5 2 4 4 1-1.5 2.5-2.5 4-2.5s3 1 4 2.5c.5-2 2-3.5 4-4C17 10 16 7 17 4c-2 1-5.5.5-5-2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                <circle cx="12" cy="12" r="2" fill="currentColor"/>
              </svg>
              {t.traditional}
            </span>
          )}
          {recipe.cuisine && <span className="tag tag--cuisine">{t.cuisines[recipe.cuisine] || recipe.cuisine}</span>}
          {displayDietaryTags.map(tag => (
            <span key={tag} className="tag tag--dietary">{t.dietaryTags[tag] || tag}</span>
          ))}
        </div>
      </div>

      <div className="recipe-page__stats">
        {totalTime > 0 && (
          <div className="stat">
            <span className="stat__label">{t.totalTime}</span>
            <span className="stat__value">{formatTime(totalTime)}</span>
          </div>
        )}
        <div className="stat stat--servings">
          <span className="stat__label">{t.serves}</span>
          <div className="stat__scaler">
            <button onClick={() => setServings(s => Math.max(1, s - 1))}>-</button>
            <span>{servings}</span>
            <button onClick={() => setServings(s => s + 1)}>+</button>
          </div>
        </div>
      </div>

      <div className="recipe-page__body">

        {displayIngredients.length > 0 && (
          <section className="recipe-page__section recipe-page__ingredients">
            <h2>{t.ingredients}</h2>
            {servings !== recipe.default_servings && (
              <p className="recipe-page__scale-note">
                {t.scaledFor(servings, recipe.servings_unit || 'servings')}
              </p>
            )}
            <ul>
              {displayIngredients.map(ing => {
                const scaled = ing.amount !== null ? ing.amount * scale : null;
                const converted = convertIngredient(scaled, ing.unit, unitSystem);
                const displayAmount = formatAmount(converted.amount);
                const displayUnit = (!converted.unit || converted.unit === 'count') ? '' : converted.unit;
                return (
                  <li key={ing.id}>
                    <span className="ingredient__amount">
                      {displayAmount}{displayUnit ? ` ${displayUnit}` : ''}
                    </span>
                    <span className="ingredient__name">
                      {ing.ingredient_name}
                      {ing.prep_notes && (
                        <span className="ingredient__notes">, {ing.prep_notes}</span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {displaySteps.length > 0 && (
          <section className="recipe-page__section recipe-page__steps">
            <div className="recipe-page__steps-header">
              <h2>{t.instructions}</h2>
              <button
                className="recipe-page__cook-btn"
                onClick={() => setShowCookingMode(true)}
              >
                {t.startCooking}
              </button>
            </div>
            <ol>
              {displaySteps.map(step => (
                <li key={step.id}>
                  <p>{convertTemp(step.instruction, unitSystem)}</p>
                  {step.duration_mins && (
                    <span className="step__time">&#9201; {formatTime(step.duration_mins)}</span>
                  )}
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>

      {nutrition && (
        <section className="recipe-page__nutrition">
          <h2>{t.nutritionTitle} <span className="nutrition__per">{t.perServing}</span></h2>
          <div className="nutrition__grid">
            {nutrition.calories && (
              <div className="nutrition__item">
                <span className="nutrition__value">{nutrition.calories}</span>
                <span className="nutrition__label">kcal</span>
              </div>
            )}
            {nutrition.protein_g && (
              <div className="nutrition__item">
                <span className="nutrition__value">{nutrition.protein_g}g</span>
                <span className="nutrition__label">{t.protein}</span>
              </div>
            )}
            {nutrition.carbs_g && (
              <div className="nutrition__item">
                <span className="nutrition__value">{nutrition.carbs_g}g</span>
                <span className="nutrition__label">{t.carbs}</span>
              </div>
            )}
            {nutrition.fat_g && (
              <div className="nutrition__item">
                <span className="nutrition__value">{nutrition.fat_g}g</span>
                <span className="nutrition__label">{t.fat}</span>
              </div>
            )}
            {nutrition.fiber_g && (
              <div className="nutrition__item">
                <span className="nutrition__value">{nutrition.fiber_g}g</span>
                <span className="nutrition__label">{t.fiber}</span>
              </div>
            )}
            {nutrition.sugar_g && (
              <div className="nutrition__item">
                <span className="nutrition__value">{nutrition.sugar_g}g</span>
                <span className="nutrition__label">{t.sugar}</span>
              </div>
            )}
          </div>
        </section>
      )}

      {displayEquipment.length > 0 && (
        <section className="recipe-page__extra">
          <h2>{t.equipmentNeeded}</h2>
          <ul className="recipe-page__equipment">
            {displayEquipment.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </section>
      )}

      {displayNotes && (
        <section className="recipe-page__extra recipe-page__notes">
          <h2>{t.notesHeading}</h2>
          <p>{displayNotes}</p>
        </section>
      )}


      {isAdmin && recipe.source_url && (
        <section className="recipe-page__extra recipe-page__source">
          <p>
            {t.recipeFrom}:{' '}
            {recipe.source_url.startsWith('http') ? (
              <a href={recipe.source_url} target="_blank" rel="noopener noreferrer">{recipe.source_url}</a>
            ) : (
              recipe.source_url
            )}
          </p>
        </section>
      )}

      {isAdmin && language === 'hu' && !translation && !translating && (
        <section className="recipe-page__extra">
          {translateError && <p className="tips-error">{translateError}</p>}
          <button className="tips-generate-btn" onClick={handleTranslate}>
            {t.translateToHungarian}
          </button>
        </section>
      )}
      {isAdmin && translating && (
        <section className="recipe-page__extra">
          <p style={{ color: '#888', fontStyle: 'italic' }}>{t.translating}</p>
        </section>
      )}


      {isAdmin && (
        <CookingNotes recipeId={recipeId} />
      )}

      {showMealPlanModal && (
        <AddToMealPlanModal
          recipeId={recipeId}
          recipeTitle={recipe.title}
          onClose={() => setShowMealPlanModal(false)}
        />
      )}

      {showCookingMode && (
        <CookingMode
          recipe={recipe}
          steps={displaySteps}
          ingredients={displayIngredients}
          scale={scale}
          unitSystem={unitSystem}
          tips={displayTips}
          onClose={() => setShowCookingMode(false)}
        />
      )}

      {showEdit && (
        <EditRecipePanel
          recipe={recipe}
          ingredients={ingredients}
          steps={steps}
          nutrition={nutrition}
          language={language}
          onClose={() => setShowEdit(false)}
          onSaved={() => {
            setShowEdit(false);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}

    </div>
  );
}

export default RecipePage;
